import { useInView } from "../hooks/useInView";
import { RevealChild } from "./Reveal";
import { FiArrowRight, FiExternalLink } from "react-icons/fi";
import { Link } from "react-router-dom";
import { certificates } from "../data/certificates";

const CertificationsTeaser = () => {
  const { ref, inView } = useInView({ threshold: 0.15 });
  const earned = certificates.filter((c) => c.id.includes("completed"));
  const inProgress = certificates.filter((c) => c.id.includes("in-progress"));

  return (
    <section className="py-12 text-light-text dark:text-text-primary" ref={ref}>
      <div className="max-w-6xl mx-auto px-6 md:px-10">
        <RevealChild visible={inView} delay={0}>
          <div className="mb-10">
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-accent mb-3">
              Certifications
            </p>
            <h2 className="text-3xl md:text-4xl font-bold mb-4 leading-tight max-w-2xl tracking-tight">
              Current signals,{" "}
              <span className="text-accent">kept intentionally focused.</span>
            </h2>
          </div>
        </RevealChild>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {/* Earned */}
          {earned.map((cert, i) => (
            <RevealChild key={cert.id} visible={inView} delay={80 + i * 80}>
              <div className="relative overflow-hidden rounded-xl border border-accent/20 bg-light-surface p-6 dark:bg-surface-1">
                <div className="absolute inset-y-0 left-0 w-1 bg-accent/60" aria-hidden="true" />
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-wider text-accent mb-1">
                      {cert.issuer}
                    </p>
                    <h3 className="text-lg font-bold">{cert.shortTitle}</h3>
                  </div>
                  <span className="inline-flex items-center rounded-full bg-accent/10 px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider text-accent">
                    Earned {cert.date}
                  </span>
                </div>
                <p className="text-sm text-light-muted dark:text-text-muted leading-relaxed mb-4">
                  {cert.notes}
                </p>
                <a
                  href={cert.credlyLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-sm font-medium text-accent hover:text-accent-muted transition-colors"
                >
                  Verify on Credly
                  <FiExternalLink size={12} />
                </a>
              </div>
            </RevealChild>
          ))}

          {/* In Progress */}
          {inProgress.length > 0 ? (
            inProgress.map((cert, i) => (
              <RevealChild key={cert.id} visible={inView} delay={80 + (earned.length + i) * 80}>
                <div className="relative overflow-hidden rounded-xl border border-light-border bg-light-surface p-6 dark:border-border-subtle dark:bg-surface-1">
                  <div className="absolute inset-y-0 left-0 w-1 bg-warning/50" aria-hidden="true" />
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <p className="text-xs font-semibold uppercase tracking-wider text-light-muted dark:text-text-faint mb-1">
                        {cert.issuer}
                      </p>
                      <h3 className="text-lg font-bold">{cert.shortTitle}</h3>
                    </div>
                    <span className="inline-flex items-center rounded-full bg-warning/10 px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider text-warning">
                      In Progress
                    </span>
                  </div>
                  <p className="text-sm text-light-muted dark:text-text-muted leading-relaxed mb-4">
                    {cert.notes}
                  </p>
                  <a
                    href={cert.vaultNotesLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 text-sm font-medium text-accent hover:text-accent-muted transition-colors"
                  >
                    Study Notes
                    <FiExternalLink size={12} />
                  </a>
                </div>
              </RevealChild>
            ))
          ) : (
            /* Fallback in-progress cards when no data */
            <>
              <RevealChild visible={inView} delay={160}>
                <div className="relative overflow-hidden rounded-xl border border-light-border bg-light-surface p-6 dark:border-border-subtle dark:bg-surface-1">
                  <div className="absolute inset-y-0 left-0 w-1 bg-warning/50" aria-hidden="true" />
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <p className="text-xs font-semibold uppercase tracking-wider text-light-muted dark:text-text-faint mb-1">
                        Cloud Native Computing Foundation
                      </p>
                      <h3 className="text-lg font-bold">CKA</h3>
                    </div>
                    <span className="inline-flex items-center rounded-full bg-warning/10 px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider text-warning">
                      In Progress
                    </span>
                  </div>
                  <p className="text-sm text-light-muted dark:text-text-muted leading-relaxed mb-4">
                    Cluster architecture, networking, storage, and troubleshooting. 40-day certification course in progress.
                  </p>
                  <a
                    href="https://brain.rakeshkadam.me"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 text-sm font-medium text-accent hover:text-accent-muted transition-colors"
                  >
                    Study Notes
                    <FiExternalLink size={12} />
                  </a>
                </div>
              </RevealChild>
              <RevealChild visible={inView} delay={240}>
                <div className="relative overflow-hidden rounded-xl border border-light-border bg-light-surface p-6 dark:border-border-subtle dark:bg-surface-1">
                  <div className="absolute inset-y-0 left-0 w-1 bg-warning/50" aria-hidden="true" />
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <p className="text-xs font-semibold uppercase tracking-wider text-light-muted dark:text-text-faint mb-1">
                        Cloud Native Computing Foundation
                      </p>
                      <h3 className="text-lg font-bold">CKS</h3>
                    </div>
                    <span className="inline-flex items-center rounded-full bg-warning/10 px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider text-warning">
                      In Progress
                    </span>
                  </div>
                  <p className="text-sm text-light-muted dark:text-text-muted leading-relaxed mb-4">
                    Kubernetes security specialist — cluster hardening, supply chain security, and runtime security.
                  </p>
                  <a
                    href="https://brain.rakeshkadam.me"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 text-sm font-medium text-accent hover:text-accent-muted transition-colors"
                  >
                    Study Notes
                    <FiExternalLink size={12} />
                  </a>
                </div>
              </RevealChild>
            </>
          )}
        </div>

        <RevealChild visible={inView} delay={320}>
          <div className="mt-10 text-center">
            <Link
              to="/certificates"
              className="group inline-flex items-center gap-2 px-5 py-2.5 border border-light-border dark:border-border-subtle rounded-lg font-medium text-sm hover:border-accent/40 hover:text-accent dark:hover:text-accent transition-colors"
            >
              View all certifications
              <FiArrowRight size={14} className="transition-transform group-hover:translate-x-0.5" />
            </Link>
          </div>
        </RevealChild>
      </div>
    </section>
  );
};

export default CertificationsTeaser;
